<template>
  <div class="contents">
    <ContactShell
      :secondary-cta-label="i18n(translations.backToOrdersCta)"
      :secondary-cta-to="
        resolveLocalizedRoute({ name: DASHBOARD.MY_ORDERS.SELF })
      "
      :title="i18n(translations.heading)"
      :withfooter-caption="false"
      @submit="submitRequest"
    >
      <template #content>
        <RevInfoBlock
          v-if="infoBlock && infoBlock.date"
          :icon="IconInfo"
          :title="
            i18n(infoBlock.title, {
              date:
                infoBlock.date instanceof Date
                  ? i18n.date(infoBlock.date)
                  : infoBlock.date,
            })
          "
          variant="info"
        >
        </RevInfoBlock>
      </template>
    </ContactShell>
  </div>
</template>

<script lang="ts" setup>
import { useRouteLocationWithLocale } from '#imports'
import { computed, watch } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { IconInfo } from '@ds/icons/IconInfo'

import type {
  ContactEvents,
  MachineSend,
} from '@/scopes/customer-care/resolution/machine/ResolutionFlow.machine.types'
import { ROUTE_NAMES } from '@/scopes/dashboard/constants'

import type { ContactFormValues } from '../../../../common/ContactShell/ContactShell.types'
import ContactShell from '../../../../common/ContactShell/ContactShell.vue'

import { getInfoBlockContentConfig } from './OrderlineLifecycleContactScreen.config'
import translations from './OrderlineLifecycleContactScreen.translations'
import type { OrderlineLifecycleContactScreenProps } from './OrderlineLifecycleContactScreen.types'

const { DASHBOARD } = ROUTE_NAMES

const props = defineProps<OrderlineLifecycleContactScreenProps>()

const i18n = useI18n()
const resolveLocalizedRoute = useRouteLocationWithLocale()
const { openErrorToast } = useTheToast()

const send = props.machineSend as MachineSend<ContactEvents>

const infoBlock = computed(() => {
  const activeState =
    props.machineState.context.orderlineLifecycleContactFormActiveState

  const config = getInfoBlockContentConfig(
    props.machineState.context.orderlineTimeline,
  )

  if (activeState && config[activeState]) return config[activeState]

  return undefined
})

const hasSubmitError = computed(() =>
  props.machineState.matches('CONTACT.SUBMISSION_ERROR'),
)

function submitRequest({ details, files }: ContactFormValues) {
  send({
    type: 'CONTACT_REFURBISHER',
    payload: {
      description: details,
      pictures: files,
    },
  })
}

watch(
  hasSubmitError,
  (isError) => {
    if (isError) {
      openErrorToast({
        title: i18n(translations.errorToastTitle),
        content: i18n(translations.errorToastDescription),
        closeCallback: () => send('GO_TO_FORM'),
      })
    }
  },
  { immediate: true },
)
</script>
